import { useState, useEffect } from 'react';
import { DEFAULT_STATE, MARKET_SITE_MAPPING } from '../config/launchConfig';

const useLaunchForm = () => {
    const [selectedServer, setSelectedServer] = useState(DEFAULT_STATE);
    const [siteList, setSiteList] = useState([]);
    const hasCredentials = Boolean(selectedServer.username && selectedServer.password);

    // Update field change handler to handle site clearing
    const handleFieldChange = (field, value) => {
        if (field === 'site') {
            // If clearing site but market exists, maintain the site options
            if (!value && selectedServer.market) {
                const sites = getSitesForMarket(selectedServer.market.externalRegMarketName);
                setSiteList(sites);
            }
        }
        setSelectedServer((prev) => ({ ...prev, [field]: value }));
    };

    // Handle game selection
    const handleGameChange = (e, newValue) => {
        setSelectedServer((prev) => ({ ...prev, game: newValue }));
    };

    const getSitesForMarket = (marketName) => {
        return MARKET_SITE_MAPPING[marketName] || [];
    };

    // Update market change handler to use helper function
    const handleMarketChange = (e, newValue) => {
        if (!newValue) {
            setSelectedServer(prev => ({ ...prev, market: null, site: null }));
            setSiteList([]);
            return;
        }

        const sites = getSitesForMarket(newValue.externalRegMarketName);
        setSelectedServer(prev => ({
            ...prev,
            market: newValue,
            site: sites.length > 0 ? sites[0] : null
        }));
        setSiteList(sites);
    };

    // Handle lobby selection
    const handleLobbyChange = (event, newValue) => {
        // If it's a manual entry (string) or an object from the list
        const lobbyValue = typeof newValue === 'string'
            ? { name: newValue }  // Create an object with name property
            : newValue;           // Use the selected object as is

        setSelectedServer(prev => ({
            ...prev,
            lobby: lobbyValue
        }));
    };

    // Handle product name selection
    const handleProductNameChange = (e, newValue) => {
        setSelectedServer((prev) => ({ ...prev, productName: newValue }));
    };

    // Handle account ID changes
    const handleAccountIdChange = (e) => {
        setSelectedServer((prev) => ({ ...prev, accountId: e.target.value }));
    };

    // Handle auth token changes
    const handleAuthTokenChange = (e) => {
        setSelectedServer((prev) => ({ ...prev, authToken: e.target.value }));
    };

    // Reset form to initial state
    const handleReset = () => {
        setSelectedServer(DEFAULT_STATE);
        setSiteList([]);
        sessionStorage.removeItem('encodedUsername');
        sessionStorage.removeItem('encodedPassword');
    };

    // Server selection from explorer
    const handleSelectServer = (server) => {
        if (!server) {
            console.error("Server data is undefined");
            return;
        }

        const sites = getSitesForMarket(server.market);
        setSiteList(sites);

        setSelectedServer((prev) => ({
            ...prev,
            serverId: server.serverId || "Unknown Server ID",
            serverName: server.serverName || "Unknown Server",
            lobby: server.lobbyName ? { name: server.lobbyName } : prev.lobby,
            market: { externalRegMarketName: server.market || "Unknown Market" },
            site: { name: server.site },
            environment: server.environment,
        }));
    };

    // Encode credentials when they change
    useEffect(() => {
        if (selectedServer.username || selectedServer.password) {
            const encodedUsername = btoa(selectedServer.username || '');
            const encodedPassword = btoa(selectedServer.password || '');
            sessionStorage.setItem('encodedUsername', encodedUsername);
            sessionStorage.setItem('encodedPassword', encodedPassword);
        }
    }, [selectedServer.username, selectedServer.password]);

    return {
        selectedServer,
        setSelectedServer,
        siteList,
        hasCredentials,
        handleFieldChange,
        handleGameChange,
        handleMarketChange,
        handleLobbyChange,
        handleProductNameChange,
        handleAccountIdChange,
        handleAuthTokenChange,
        handleReset,
        handleSelectServer
    };
};

export default useLaunchForm;